import axios from "@/libs/axios";
import { useChatsStore } from "@/store/useChatsStore";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { Tag, TagDto } from "@saysimple-platform/dtos";
import { useQuery } from "@tanstack/vue-query";
import { AxiosResponse } from "axios";
import { defineStore } from "pinia";

interface State {
    isLoadingTags: boolean;
    isLoadingTag: boolean;
    tags: TagDto[];
    activeChatTags: TagDto[];
    isLoadingActiveTags: boolean;
    isProcessingTag: boolean; // used for (un)linking
}

export const useTagsStore = defineStore("tags", {
    state: (): State => ({
        isLoadingTags: false,
        isLoadingTag: false,
        tags: [],
        activeChatTags: [],
        isLoadingActiveTags: false,
        isProcessingTag: false,
    }),
    actions: {
        fetchTags(requestQueryBuilder?: RequestQueryBuilder): void {
            useQuery<Pick<AxiosResponse<Tag[]>, "data">>(
                ["tags", requestQueryBuilder?.queryObject],
                () => {
                    this.isLoadingTags = true;
                    return axios.get<Tag[]>(`${process.env.VUE_APP_PUBLIC_API}/tags`, {
                        params: requestQueryBuilder?.queryObject,
                    });
                },
                {
                    staleTime: 300000, // 5min
                    placeholderData: { data: [] },
                    onSuccess: (response) => (this.tags = response.data),
                    onSettled: () => (this.isLoadingTags = false),
                }
            );
        },
        async fetchChatTags(chatId: number, requestQueryBuilder?: RequestQueryBuilder) {
            // @NOTE function is called in a watch, so anonymous function scope what result in issues with vue-query.

            //useQuery<Pick<AxiosResponse<Tag[]>, "data">>(
            //    ["tags", chatId, requestQueryBuilder?.queryObject],
            //    () => {
            //        this.isLoadingActiveTags = true;
            //        return axios.get<Tag[]>(`${process.env.VUE_APP_PUBLIC_API}/messaging-chats/${chatId}/tags`, {
            //            params: requestQueryBuilder?.queryObject,
            //        });
            //    },
            //    {
            //        staleTime: 300000, // 5min.
            //        placeholderData: { data: [] },
            //        onSuccess: (response) => (this.tags = response.data),
            //        onSettled: () => (this.isLoadingActiveTags = false),
            //    }
            //);

            this.isLoadingActiveTags = true;

            // Can't useQuery to work, because its called outside setup somewhere probably somewhere...
            const { data } = await axios.get(`${process.env.VUE_APP_PUBLIC_API}/messaging-chats/${chatId}/tags`, {
                params: requestQueryBuilder?.queryObject,
            });

            this.activeChatTags = data;
            this.isLoadingActiveTags = false;
        },
        createTag(tag: Tag): Promise<Tag> {
            this.isLoadingTag = true;

            return axios
                .post(`${process.env.VUE_APP_PUBLIC_API}/tags`, tag)
                .then(({ data }) => {
                    this.tags.unshift(data);

                    return data;
                })
                .finally(() => (this.isLoadingTag = false));
        },
        updateTag(tagId: number, partialTag: Partial<Tag>): Promise<Tag> {
            this.isLoadingTag = true;

            const index = this.tags.findIndex((findTag: Tag) => findTag.id === tagId);

            const tag: Tag = {
                ...this.tags[index],
                ...partialTag,
            };

            return axios
                .patch(`${process.env.VUE_APP_PUBLIC_API}/tags/${tagId}`, tag)
                .then((res) => {
                    this.tags.splice(index, 1, res.data);
                    return res.data;
                })
                .finally(() => (this.isLoadingTag = false));
        },
        deleteTag(tagId: number): Promise<number> {
            this.isLoadingTag = true;

            return axios
                .delete(`${process.env.VUE_APP_PUBLIC_API}/tags/${tagId}`)
                .then(({ data }) => {
                    const index = this.tags.findIndex((findTag: Tag) => findTag.id === tagId);
                    this.tags.splice(index, 1);

                    return data.affected;
                })
                .finally(() => (this.isLoadingTag = false));
        },
        linkTag(tag: TagDto): Promise<TagDto> {
            const chatsStore = useChatsStore();
            this.isProcessingTag = true;

            const tagIds = this.activeChatTags?.map((tag) => ({ tagId: tag.id }));
            tagIds?.push({
                tagId: tag.id,
            });

            return axios
                .post<TagDto>(
                    `${process.env.VUE_APP_PUBLIC_API}/messaging-chats/${chatsStore.activeChat?.chat.id}/tags`,
                    tagIds
                )
                .then((response) => {
                    this.activeChatTags?.push(tag);

                    return response.data;
                })
                .finally(() => (this.isProcessingTag = false));
        },
        unlinkTag(tag: TagDto): Promise<TagDto> {
            const chatsStore = useChatsStore();
            this.isProcessingTag = true;

            // We clone the active chat, so when the backend request fails, we still have the original state.
            const tagIds = this.activeChatTags?.map((tag) => ({ tagId: tag.id }));
            const indexToRemove = tagIds.findIndex((activeChatTag) => activeChatTag.tagId === tag.id);
            tagIds.splice(indexToRemove, 1);
            return axios
                .post<TagDto>(
                    `${process.env.VUE_APP_PUBLIC_API}/messaging-chats/${chatsStore.activeChat?.chat.id}/tags`,
                    tagIds
                )
                .then((response) => {
                    this.activeChatTags.splice(indexToRemove, 1);

                    return response.data;
                })
                .finally(() => (this.isProcessingTag = false));
        },
    },
    getters: {
        hasTags(state) {
            return state.activeChatTags.length > 0;
        },
    },
});
